import React, { useState, useEffect } from 'react';
import Header from './Header';
import './scss/teamDirectory.scss';
import api from './services/apiService';
import { useNotification } from './components/NotificationProvider';
import TeamDetailsModal from './components/TeamDetailsModal';

function TeamDirectory() {
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showError } = useNotification();

  // Generate A-Z alphabet array
  const alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 
                   'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    filterTeams();
  }, [teams, searchTerm, selectedLetter]);

  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_teams_directory.php`);
      
      if (response.data.success) {
        setTeams(response.data.teams);
      } else {
        showError('Failed to load teams directory', 5000);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      showError('Error loading teams directory', 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const filterTeams = () => {
    let filtered = teams;

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(team => 
        team.team_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by selected letter
    if (selectedLetter !== 'all') {
      filtered = filtered.filter(team => 
        team.team_name.toUpperCase().startsWith(selectedLetter)
      );
    }

    // Sort alphabetically
    filtered.sort((a, b) => a.team_name.localeCompare(b.team_name));

    setFilteredTeams(filtered);
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(letter);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedLetter('all');
  };

  const handleTeamClick = (team) => {
    setSelectedTeam(team);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeam(null);
  };

  return (
    <div className="TeamDirectory">
      <Header />
      <div className="container">
        <div className="directory-header">
          <h1>Team Directory</h1>
          <p>Browse and search for teams in the FirstWhistle network</p>
        </div>

        <div className="search-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <button 
              className="clear-filters-btn"
              onClick={clearFilters}
              disabled={!searchTerm && selectedLetter === 'all'}
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="alphabet-filter">
          <button 
            className={`letter-btn ${selectedLetter === 'all' ? 'active' : ''}`}
            onClick={() => handleLetterClick('all')}
          >
            All
          </button>
          {alphabet.map(letter => (
            <button
              key={letter}
              className={`letter-btn ${selectedLetter === letter ? 'active' : ''}`}
              onClick={() => handleLetterClick(letter)}
            >
              {letter}
            </button>
          ))}
        </div>

        <div className="teams-section">
          {isLoading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <p>Loading teams...</p>
            </div>
          ) : (
            <>
              <div className="results-info">
                <p>
                  Showing {filteredTeams.length} of {teams.length} teams
                  {searchTerm && ` matching "${searchTerm}"`}
                  {selectedLetter !== 'all' && ` starting with "${selectedLetter}"`}
                </p>
              </div>

              {filteredTeams.length === 0 ? (
                <div className="no-results">
                  <p>No teams found matching your criteria.</p>
                  <button onClick={clearFilters} className="clear-filters-btn">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="teams-grid">
                  {filteredTeams.map(team => (
                    <div key={team.team_id} className="team-card" onClick={() => handleTeamClick(team)}>
                      <div className="team-logo">
                        <img 
                          src={`https://football.pebbledesigns.co.uk/api/images/teams/${team.team_id}/logo-light.png`}
                          alt={`${team.team_name} logo`}
                          onError={(e) => {
                            // Fallback to dark version if light doesn't exist
                            e.target.src = `https://football.pebbledesigns.co.uk/api/images/teams/${team.team_id}/logo-dark.png`;
                            e.target.onerror = () => {
                              // Final fallback to default logo at root level
                              e.target.src = `https://football.pebbledesigns.co.uk/api/images/teams/logo-light.png`;
                              e.target.onerror = () => {
                                // Hide image if no logo exists
                                e.target.style.display = 'none';
                              };
                            };
                          }}
                        />
                      </div>
                      <div className="team-info">
                        <h3 className="team-name">{team.team_name}</h3>
                        {team.location && (
                          <p className="team-location">{team.location}</p>
                        )}
                        {team.age_group && (
                          <p className="team-age-group">{team.age_group}</p>
                        )}
                        {team.manager_name && (
                          <p className="team-manager">Manager: {team.manager_name.split(' ').map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()).join(' ')}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <TeamDetailsModal 
        team={selectedTeam}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
}

export default TeamDirectory; 